/**
 * Temporal Parking Test Page
 * Standalone page for testing temporal parking algorithm with visual feedback
 */

import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import { PlanningResultWithData } from "./types";
import { getFreshTestData, TEST_SCENARIOS } from "./testData";
import { planParkingAssignments } from "./temporalPlanner";
import { TimelineView } from "./components/TimelineView";
import { DecisionLogDisplay } from "./components/DecisionLogDisplay";
import { PlacementList } from "./components/PlacementList";
import { ParkingConfigForm } from "./components/ParkingConfigForm";
import { useParkingConfig } from "./hooks/useParkingConfig";
import { countTrappedAircraft } from "./egress";
import { precomputeAllEgressMasks } from "./egressMasks";

export const TemporalTest: React.FC = () => {
  const [result, setResult] = React.useState<PlanningResultWithData | null>(null);
  const [selectedScenario, setSelectedScenario] = React.useState("basic");
  const [bufferMinutes, setBufferMinutes] = React.useState(15);
  const [plannedMoveCap, setPlannedMoveCap] = React.useState(0);
  const [isRunning, setIsRunning] = React.useState(false);

  // Use shared parking config hook
  const parkingConfig = useParkingConfig({
    bufferMinutes: bufferMinutes,
    plannedMoveCap: plannedMoveCap,
    allowTrapping: true,
    egressWeight: 1.0,
    wasteWeight: 1.0,
    structuralWeight: 0.1,
    sortingStrategy: 'arrival',
    zonePriorityWeight: 1.0,
  });

  const currentScenario = TEST_SCENARIOS.find((s) => s.id === selectedScenario) || TEST_SCENARIOS[0];

  // Reset results when parameters change
  React.useEffect(() => {
    setResult(null);
  }, [selectedScenario, bufferMinutes, plannedMoveCap, parkingConfig.config]);

  const handleRunPlanner = () => {
    setIsRunning(true);

    // Get fresh test data (resets bitmaps)
    const { aircraft, spots, zones, horizonHours, startTime } = getFreshTestData(selectedScenario);

    // Run the planner with current settings
    const planningResult = planParkingAssignments(
      aircraft,
      spots,
      bufferMinutes,
      plannedMoveCap,
      parkingConfig.config.allowTrapping,
      parkingConfig.config.egressWeight,
      parkingConfig.config.wasteWeight,
      parkingConfig.config.structuralWeight,
      parkingConfig.config.sortingStrategy,
      horizonHours,
      undefined,  // shapes (for geometry collision)
      true,       // useMaskOptimization - Phase 9 fast path
      false,      // validateMasks - validation not needed (proven correct). TODO: remove validation parameter entirely in future
      parkingConfig.config.zonePriorityWeight,
      zones,
      parkingConfig.config.min_dwell_weight
    );

    // Precompute egress masks for visualization and analysis
    // WHY: Allows fast egress blocking checks and powers the blocked spot visualization on timeline
    // WHEN: After placement complete, before rendering results
    precomputeAllEgressMasks(spots, planningResult.placement, aircraft, bufferMinutes);

    // Calculate trap statistics for display in stats panel
    // WHY: Users need high-level overview of how many egress conflicts exist
    // USE CASE: "3 aircraft are blocking 8 others" tells operator there's a problem
    let trappingCount = 0;        // How many aircraft are creating traps
    let totalTrappedAircraft = 0; // How many aircraft are trapped (sum)

    for (const aircraftId in planningResult.placement) {
      const ac = aircraft.find((a) => a.id === aircraftId);
      const placementInfo = planningResult.placement[aircraftId];

      // Skip segmented placements for now
      // WHY: Trap detection for segments needs special handling (future enhancement)
      if (!ac || !("spotId" in placementInfo)) continue;

      const spot = spots.find((s) => s.id === placementInfo.spotId);
      if (!spot) continue;

      // Check how many aircraft this placement traps
      // WHY: Some placements trap 0, some trap many - we need to count each
      const trapCount = countTrappedAircraft(
        ac,                       // Aircraft we placed
        spot,                     // Where we placed it
        planningResult.placement, // All placements
        aircraft,                 // All aircraft (for look-ahead)
        spots                     // All spots (for blocking)
      );

      // If this aircraft traps at least one other, count it
      // WHY: trappingCount = "problematic placements", totalTrappedAircraft = "total impact"
      if (trapCount > 0) {
        trappingCount++;                  // One more problematic placement
        totalTrappedAircraft += trapCount; // Add to total impact
      }
    }

    // Store everything together in state with trap statistics
    setResult({
      ...planningResult,
      stats: {
        ...planningResult.stats,
        trappingCount,
        totalTrappedAircraft,
      },
      aircraft,
      spots,
      zones,
      horizonHours,
      startTime,
    });

    setIsRunning(false);
  };

  // Check if results match expectations
  const resultsMatch =
    result &&
    result.stats.placed === currentScenario.expectedPlaced &&
    result.stats.unplaced === currentScenario.expectedUnplaced;

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h5" gutterBottom>
        Temporal Parking Test
      </Typography>

      <Stack spacing={1.5}>
        {/* Core Settings Row */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: 300 }} size="small">
            <InputLabel>Test Scenario</InputLabel>
            <Select
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              label="Test Scenario"
            >
              {TEST_SCENARIOS.map((scenario) => (
                <MenuItem key={scenario.id} value={scenario.id}>
                  {scenario.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ParkingConfigForm
            {...parkingConfig.config}
            onBufferMinutesChange={(value) => {
              setBufferMinutes(value);
              parkingConfig.setBufferMinutes(value);
            }}
            onPlannedMoveCapChange={(value) => {
              setPlannedMoveCap(value);
              parkingConfig.setPlannedMoveCap(value);
            }}
            onAllowTrappingChange={parkingConfig.setAllowTrapping}
            onEgressWeightChange={parkingConfig.setEgressWeight}
            onWasteWeightChange={parkingConfig.setWasteWeight}
            onStructuralWeightChange={parkingConfig.setStructuralWeight}
            onZonePriorityWeightChange={parkingConfig.setZonePriorityWeight}
            onMinDwellWeightChange={parkingConfig.setMinDwellWeight}
            onSortingStrategyChange={parkingConfig.setSortingStrategy}
            size="small"
            layout="row"
          />

          <Button
            data-testid="run-planner-button"
            variant="contained"
            color="primary"
            onClick={handleRunPlanner}
            disabled={isRunning}
            startIcon={<PlayArrowIcon />}
          >
            {isRunning ? "Running..." : "Run Planner"}
          </Button>
        </Stack>

        {result && (
          <Paper
            data-testid="stats"
            elevation={1}
            sx={{
              p: 1,
              bgcolor: resultsMatch ? "success.50" : "warning.50",
              borderColor: resultsMatch ? "success.200" : "warning.200",
              display: "inline-flex",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="subtitle2">Planning Statistics</Typography>
                {resultsMatch ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <WarningIcon color="warning" fontSize="small" />
                )}
              </Stack>
              <Typography variant="body2">
                <strong>Total Aircraft:</strong> {result.stats.totalAircraft}
              </Typography>
              <Typography variant="body2" color="success.main">
                <strong>Placed:</strong> {result.stats.placed}
              </Typography>
              <Typography variant="body2" color="error.main">
                <strong>Unplaced:</strong> {result.stats.unplaced}
              </Typography>
              {result.stats.plannedMoves > 0 && (
                <Typography variant="body2" color="info.main">
                  <strong>Planned Moves:</strong> {result.stats.plannedMoves}
                </Typography>
              )}
              {/* Show trap statistics if any traps exist
                  WHY: Operators need to know if egress blocking is a problem
                  EXAMPLE: "Traps: 2 aircraft blocking 5 others" → operator knows to investigate
                  COLOR: Warning (yellow/orange) because traps are concerning but not failures */}
              {result.stats.trappingCount !== undefined && result.stats.trappingCount > 0 && (
                <Typography variant="body2" color="warning.main">
                  <strong>Traps:</strong> {result.stats.trappingCount} aircraft blocking{" "}
                  {result.stats.totalTrappedAircraft} others
                </Typography>
              )}
              {/* Show "No Traps" when trappingCount === 0
                  WHY: Positive feedback that egress blocking is working correctly
                  USE CASE: After fix, seeing "No Traps ✓" confirms the issue is resolved */}
              {result.stats.trappingCount === 0 && result.stats.placed > 0 && (
                <Typography variant="body2" color="success.main">
                  <strong>Traps:</strong> None ✓
                </Typography>
              )}
              <Typography
                variant="caption"
                color={resultsMatch ? "success.dark" : "warning.dark"}
              >
                Expected: {currentScenario.expectedPlaced} placed / {currentScenario.expectedUnplaced} unplaced
                {resultsMatch ? " ✓" : " (mismatch)"}
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* Scenario Description */}
        <Typography variant="caption" color="text.secondary">
          {currentScenario.description}
        </Typography>

        {/* Instructions - Only show before running planner */}
        {!result && (
          <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary", my: 1 }}>
            Select a scenario and click "Run Planner" to generate parking assignments
          </Typography>
        )}

        {/* Parameter Explanations - Only show before running planner */}
        {!result && (
          <Paper sx={{ p: 2, bgcolor: "grey.50", border: 1, borderColor: "grey.300" }} elevation={0}>
            <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
              <strong>Parameter Guide</strong>
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Buffer
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Extra time (minutes) added before arrival and after departure to prevent tight spacing conflicts.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", pl: 1, borderLeft: "2px solid", borderColor: "grey.400" }}>
                  Example: Aircraft A departs at 10:00. With 15min buffer, spot becomes available at 10:15, preventing rushed turnarounds.
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Planned Moves
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Maximum number of times an aircraft can be relocated during its dwell. 0 = no moves (aircraft stays in one spot).
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", pl: 1, borderLeft: "2px solid", borderColor: "grey.400" }}>
                  Example: Aircraft arrives 8:00, departs 16:00. With 1 move allowed, it could park at Spot A (8:00-12:00) then move to Spot B (12:00-16:00) if no single spot is available for the full 8 hours.
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Allow Trapping
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  When checked, allows placing aircraft in spots that block others' departure paths. When unchecked, only places aircraft in spots that don't trap others (may leave aircraft unplaced).
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", pl: 1, borderLeft: "2px solid", borderColor: "grey.400" }}>
                  Example: Row has spots A (front) and B (back). Aircraft at B departs at 14:00, aircraft at A departs at 16:00. With trapping disallowed, B cannot be used (would be blocked). With trapping allowed, both spots can be used.
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Egress Weight
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Controls how strongly the algorithm avoids placements that trap other aircraft.
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Low (1x):</strong> Minimal penalty - prefers spots without traps but accepts trapping when needed
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Medium (2x):</strong> Moderate penalty - works harder to avoid traps, may leave aircraft unplaced
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>High (5x):</strong> Strong penalty - heavily prioritizes zero-trap placements over placement count
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", pl: 1, borderLeft: "2px solid", borderColor: "grey.400" }}>
                  Example: Spot A traps 2 aircraft (score = 2 × weight), Spot B traps 0 (score = 0). With High weight (5x), Spot A scores 10 vs Spot B scores 0, making Spot B strongly preferred.
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Waste Weight
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Controls how strongly the algorithm avoids placing small aircraft in oversized spots. Helps reserve premium large spots for large aircraft that actually need them.
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Disabled (0x):</strong> No penalty - ignores spot size mismatch, algorithm places freely
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Low (0.5x):</strong> Gentle nudge - slight preference for right-sized spots
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Medium (1x):</strong> Balanced penalty - comparable to egress weight
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>High (2x):</strong> Strong preference - works hard to avoid oversized spots
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Very High (3x):</strong> Extreme penalty - aggressively reserves large spots for large aircraft
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", pl: 1, borderLeft: "2px solid", borderColor: "grey.400" }}>
                  Example: Citation (small) considering large-capable spot has waste=3. With Medium weight (1x), waste penalty=3. With High weight (2x), penalty=6. If a right-sized small-only spot has score=0 and large spot has egress penalty=2, then: Medium makes scores 0 vs 5 (prefer small), High makes scores 0 vs 8 (strongly prefer small).
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Sorting Strategy
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Controls the order in which aircraft are processed by the greedy placement algorithm. Different orderings can dramatically affect placement rates.
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Arrival Order:</strong> Process by arrival time (earliest first). Realistic but may waste flexible spots.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Size-Constrained First:</strong> Place aircraft with fewer compatible spots first. Prevents wasting flexible spots on flexible aircraft.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Longest Dwell First:</strong> Place longest-staying aircraft first. Ensures long dwells get priority.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Latest Departure First:</strong> Place latest-departing aircraft first. Optimizes overnight parking.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Hybrid:</strong> Combines size-constrained and longest-dwell (most constrained + longest first).
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", pl: 1, borderLeft: "2px solid", borderColor: "grey.400" }}>
                  Example: 2 spots (A=Small-only, B=Flex). Small aircraft arrives first, greedy places it at B (wastes flexibility). Large aircraft arrives, rejected (only B works, now occupied). With size-constrained ordering: Large placed first at B, small takes A → both placed!
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Size Categories
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Aircraft are assigned size categories based on wingspan. Spots have policies that restrict which aircraft sizes they accept, enabling flexible parking rules.
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>VS (Very Small):</strong> ~35-40ft wingspan (Cessna 172, Piper PA-28, Cirrus SR22)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>S (Small):</strong> ~50-60ft wingspan (Citations, Phenom 300, King Air 350, Learjet 45)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>M (Midsize):</strong> ~60-70ft wingspan (Challenger 604/605, Falcon 900, Gulfstream G280)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>SM (Super-Midsize):</strong> ~75-100ft wingspan (Falcon 7X, Gulfstream G500/G650)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>L (Large):</strong> ~100-120ft wingspan (Global 6000/7500, Gulfstream G700, BBJ)
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  <strong>Spot Policies:</strong> Each spot has an allowed sizes list that determines which aircraft can park there.
                </Typography>
                <Stack spacing={0.5} sx={{ pl: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Universal:</strong> [VS, S, M, SM, L] - accepts all aircraft sizes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Large-only:</strong> [L] - reserves spot for large aircraft (prevents waste)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • <strong>Common jets:</strong> [S, M] - blocks very-small and large aircraft
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic", pl: 1, borderLeft: "2px solid", borderColor: "grey.400" }}>
                  Example: Spot A01 allows [S, M] (small and midsize only). A large aircraft (L) arrives but cannot use A01 due to size restrictions. It must find a spot that allows large aircraft.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        {result && (
          <>
            <PlacementList
              placement={result.placement}
              aircraft={result.aircraft}
              spots={result.spots}
              zones={result.zones}
            />

            <TimelineView
              spots={result.spots}
              aircraft={result.aircraft}
              placement={result.placement}
              log={result.log}
              horizonHours={result.horizonHours}
              startTime={result.startTime}
            />

            {/* Decision Log */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Decision Log ({result.log.length} events)
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                {result.aircraft.map((ac) => {
                  const acLogs = result.log.filter((e) => e.aircraftId === ac.id);
                  if (acLogs.length === 0) return null;

                  return (
                    <Box key={ac.id} sx={{ mb: 2 }}>
                      <DecisionLogDisplay aircraft={ac} log={result.log} />
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </>
        )}

      </Stack>
    </Container>
  );
};